import type { ApiResponse, Ticket, Appeal, User, AuthSession, PaymentIntent } from '@tykkets/shared'

const BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')

// In-memory token — restored from localStorage on module load (persists across app restarts)
// so it survives Vue Router navigations within the same tab
let accessToken: string | null = null
try {
  accessToken = localStorage.getItem('_tk') ?? sessionStorage.getItem('_tk')  // migrate from sessionStorage
} catch {}

export function setAccessToken(token: string | null) {
  accessToken = token
  try {
    if (token) { localStorage.setItem('_tk', token); sessionStorage.setItem('_tk', token) }
    else        { localStorage.removeItem('_tk'); sessionStorage.removeItem('_tk') }
  } catch {}
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  })

  if (res.status === 401) {
    // Try refresh if we have a token
    const refreshed = await tryRefresh()
    if (refreshed && accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
      const retry = await fetch(`${BASE}${path}`, { ...options, headers, credentials: 'include' })
      return retry.json()
    }
    // Refresh failed - return the 401 so callers can handle it
  }

  return res.json()
}

async function tryRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/api/auth/refresh`, { method: 'POST', credentials: 'include' })
    const data = await res.json()
    if (data.success) { accessToken = data.data.accessToken; return true }
  } catch {}
  return false
}

async function upload<T>(path: string, formData: FormData): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {}
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST', body: formData, headers, credentials: 'include',
  })
  return res.json()
}

// ── Auth headers helper (for raw fetch calls outside api.ts) ─────────────────
export function getAuthHeaders(): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) h['Authorization'] = `Bearer ${accessToken}`
  return h
}

// ── Fines ─────────────────────────────────────────────────────────────────────
export const fines = {
  analyse: (type: string, answers: Record<string, unknown>, ticketId?: string, caseRef?: string) =>
    request<unknown>(`/api/fines/${type}/analyse`, {
      method: 'POST',
      body:   JSON.stringify({ answers, ticketId, caseRef }),
    }),
  pricing: (type: string) =>
    request<unknown[]>(`/api/fines/${type}/pricing`),
  letter: (type: string, stage: string, answers: Record<string, unknown>) =>
    request<{ letterMarkdown: string }>(`/api/fines/${type}/letter`, {
      method: 'POST',
      body:   JSON.stringify({ stage, answers }),
    }),
}

// ── Appeals history ──────────────────────────────────────────────────────────
export interface AppealHistoryItem {
  id:            string
  source:        'ticket' | 'standalone'
  fine_type:     string | null
  stage:         string | null
  status:        string
  outcome:       string | null
  case_ref:      string | null
  created_at:    string
  has_letter:    boolean
  pcn_number:    string | null
  issued_by:     string | null
  fine_amount:   string | null
  ticket_id:     string | null
  ticket_status: string | null
}

export const appealsHistory = {
  list: () => request<AppealHistoryItem[]>('/api/appeals/appeals'),
}

// ── News ─────────────────────────────────────────────────────────────────────
export const news = {
  getPublic: () => request<{ id: string; headline: string; body: string; category: string; icon: string; pinned: boolean; created_at: string }[]>('/api/admin/news/public'),
}

// ── Auth ──────────────────────────────────────────────────────────────────────
export const auth = {
  requestMagicLink: (email: string, market = 'uk') =>
    request<{ message: string }>('/api/auth/magic-link', {
      method: 'POST', body: JSON.stringify({ email, market }),
    }),
  verify: (token: string) =>
    request<AuthSession>(`/api/auth/verify?token=${token}`),
  me: () =>
    request<User>('/api/auth/me'),
  logout: () =>
    request<{ message: string }>('/api/auth/logout', { method: 'POST' }),
}

// ── Tickets ───────────────────────────────────────────────────────────────────
export const tickets = {
  upload: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return upload<{ ticketId: string; status: string }>('/api/tickets/upload', fd)
  },
  list: () =>
    request<Ticket[]>('/api/tickets'),
  get: (id: string) =>
    request<Ticket>(`/api/tickets/${id}`),
  delete: (id: string) =>
    request<{ deleted: boolean }>(`/api/tickets/${id}`, { method: 'DELETE' }),
  poll: async (id: string, maxWait = 90_000): Promise<Ticket> => {
    const start = Date.now()
    while (Date.now() - start < maxWait) {
      const res = await tickets.get(id)
      if (res.success && ['analysed', 'error', 'unsupported'].includes(res.data.status)) return res.data
      await new Promise(r => setTimeout(r, 2000))
    }
    throw new Error('Analysis timed out')
  },
}

// ── Appeals ───────────────────────────────────────────────────────────────────
export const appeals = {
  generate: (ticketId: string) =>
    request<{ appealId: string; letterMarkdown: string }>('/api/appeals/generate', {
      method: 'POST', body: JSON.stringify({ ticketId }),
    }),
  get: (id: string) =>
    request<Appeal>(`/api/appeals/${id}`),
  update: (id: string, letterMarkdown: string) =>
    request<{ updated: boolean }>(`/api/appeals/${id}`, {
      method: 'PATCH', body: JSON.stringify({ letterMarkdown }),
    }),
  submit: (id: string) =>
    request<{ submitted: boolean; reference: string; method: string }>(`/api/appeals/${id}/submit`, {
      method: 'POST',
    }),
  recordOutcome: (id: string, outcome: 'won' | 'lost', notes?: string) =>
    request<{ recorded: boolean }>(`/api/appeals/${id}/outcome`, {
      method: 'POST', body: JSON.stringify({ outcome, notes }),
    }),
}

// ── Payments ──────────────────────────────────────────────────────────────────

interface PricingData {
  currency:    string
  symbol:      string
  taxLabel:    string
  fineContext: string
  appealPrice: number
}

interface PaymentHistoryItem {
  id:         string
  type:       string
  amount:     number
  currency:   string
  status:     string
  created_at: string
}

export const payments = {
  checkStatus: (intentId: string) =>
    request<{ status: string }>(`/api/payments/status/${intentId}`),
  createIntent: (opts: { fineType: string; stage: string; ticketId?: string; caseRef?: string; consentGiven?: boolean; lowMeritAcknowledged?: boolean }) =>
    request<PaymentIntent>('/api/payments/intent', {
      method: 'POST', body: JSON.stringify(opts),
    }),
  getPricing: () =>
    request<PricingData>('/api/payments/pricing'),
  getHistory: () =>
    request<PaymentHistoryItem[]>('/api/payments/history'),
  refund: (paymentId: string) =>
    request<{ refunded: boolean }>('/api/payments/refund', {
      method: 'POST', body: JSON.stringify({ paymentId }),
    }),
}

// ── Vehicles ──────────────────────────────────────────────────────────────────

export interface Vehicle {
  id:            string
  vrm:           string
  vrmDisplay:    string
  nickname?:     string
  make?:         string
  colour?:       string
  year?:         number
  verified:      boolean
  fuelType?:     string
  co2Emissions?: number
  euroStatus?:   string
  typeApproval?: string
  createdAt:     string
}

export interface CazCheckResult {
  vrm:                  string
  euroStatus:           string | null
  fuelType:             string | null
  fromCache:            boolean
  exemptionRecommended: boolean
  exemptionType:        string | null
  exemptionReason:      string | null
}

export const vehicles = {
  list: () =>
    request<Vehicle[]>('/api/vehicles'),
  add: (vrm: string, nickname?: string) =>
    request<Vehicle>('/api/vehicles', {
      method: 'POST', body: JSON.stringify({ vrm, nickname }),
    }),
  update: (id: string, nickname: string) =>
    request<Vehicle>(`/api/vehicles/${id}`, {
      method: 'PATCH', body: JSON.stringify({ nickname }),
    }),
  remove: (id: string) =>
    request<{ deleted: boolean }>(`/api/vehicles/${id}`, { method: 'DELETE' }),
  check: (vrm: string) =>
    request<{ matched: boolean; requiresPayment: boolean; vehicle?: Vehicle }>(
      `/api/vehicles/check/${encodeURIComponent(vrm)}`
    ),
  cazCheck: (vrm: string) =>
    request<CazCheckResult>(`/api/vehicles/caz-check/${encodeURIComponent(vrm)}`),
}
